import React from 'react';

import { intlPluralizeString } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, type ButtonProps } from '@gitbook/spine-react';

import { AddCommentPopover } from '../popovers/AddCommentPopover';
import { NodeCommentsPopover } from '../popovers/NodeCommentsPopover';
import { type CommentUIIntent, type UseCommentsProps, useComments } from '../useComments';

export type CommentButtonProps = Omit<ButtonProps, 'onPress'> &
    UseCommentsProps & {
        /** Whether to shorten the label to just the comment count */
        shortenLabel?: boolean;

        /** Intent to open the comments UI with */
        intent?: CommentUIIntent;
    };

/**
 * Formats the comment count label based on whether it should be shortened
 */
function formatCommentCountLabel(
    count: number,
    pluralizedLabel: string,
    shortenLabel: boolean
): string {
    return shortenLabel ? `${count}` : `${count} ${pluralizedLabel}`;
}

/**
 * Customizable button that opens a comment popover when clicked.
 * Handles all the comment creation state internally.
 */
export const CommentButton = React.memo(function CommentButton(props: CommentButtonProps) {
    const {
        nodeKey,
        tableCell,
        label,
        shortenLabel = false,
        kind = 'secondary',
        size = 'small',
        iconOnly = false,
        intent,
        ...rest
    } = props;

    const { commentCount, hasComments, openNodeComments, nodeState } = useComments({
        nodeKey,
        tableCell,
    });

    const pluralizedLabel = intlPluralizeString({
        singular: 'open comment',
        plural: 'open comments',
        count: commentCount.topLevel,
    });

    const formattedLabel = formatCommentCountLabel(
        commentCount.topLevel,
        pluralizedLabel,
        shortenLabel
    );

    /* Show a button when there are existing comments, wrapped in a popover that displays these comments + allows to add a new comment */
    if (hasComments) {
        return (
            <NodeCommentsPopover
                nodeKey={nodeKey}
                tableCell={tableCell}
                trigger={
                    <Button
                        kind={kind}
                        size={size}
                        leadingIcon={Icon.Comment}
                        title={`${commentCount.topLevel} ${pluralizedLabel}`}
                        onPress={() => openNodeComments(intent)}
                        label={formattedLabel}
                        preventFocusOnPress
                        active={nodeState.readPopoverIsOpen}
                        {...rest}
                    />
                }
            />
        );
    }

    /* Show a button when there are no existing comments, wrapped in a popover that allows to add a new comment */
    return (
        <AddCommentPopover
            nodeKey={nodeKey}
            tableCell={tableCell}
            placement="right-start"
            trigger={
                <Button
                    kind={kind}
                    data-exclude-from-copy
                    size={size}
                    leadingIcon={Icon.CommentAdd}
                    onPress={() => openNodeComments(intent)}
                    label={label}
                    iconOnly={iconOnly}
                    preventFocusOnPress
                    active={nodeState.addNewPopoverIsOpen}
                    {...rest}
                />
            }
        />
    );
});
